import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';
import { getPaginationUrl } from '../../utils/constants/GetPaginationURL';

const initialState = {
  loading: false,
  resData: {
    data: [],
    meta: {},
  },
  // SkillX assignments CRUD (chief mentor / employee side). Kept separate from
  // resData so the dashboard and the assignments grid don't stomp each other.
  assignments: {
    data: [],
    meta: {},
  },
  // XP configuration — the point table an admin edits. Unpaginated: it is a
  // fixed-size config table, not a data set.
  xpRules: {
    data: [],
    meta: {},
  },
  // Manual XP awards — the mentor audit history, server-paginated. `meta`
  // carries the header figures (XP still standing, revoked count, ...).
  xpAwards: {
    data: [],
    meta: {},
  },
  // Common badges — tier 2 of the badge model. `catalogue` is what can be
  // awarded (the achievement_badges rows with no assignment behind them);
  // `roster` is one batch's learners with the badges they currently hold.
  commonBadges: {
    catalogue: [],
    roster: [],
    loading: false,
  },
  // SkillX students — every learner enrolled on a SkillX course, with the
  // SkillX id they have been given. `summary` counts the whole date range, not
  // the current page, so "still to link" stays meaningful while paging.
  students: {
    data: [],
    meta: {},
    summary: { total: 0, linked: 0, unlinked: 0 },
    loading: false,
  },
  // Attendance register — the CRO's month grid for one batch. `days` is how
  // many columns to draw; `meta` also carries why the grid may be empty.
  attendance: {
    batches: [],
    rows: [],
    meta: {},
    loading: false,
  },
  // Referral leads — the sales view of what SkillX learners have referred.
  // `summary` counts everything in scope; `data`/`meta` are the current page.
  referralLeads: {
    data: [],
    meta: {},
    summary: { total: 0, new: 0, inProgress: 0, admitted: 0, closed: 0, conversionRate: 0 },
    loading: false,
  },
  // Admission verification — one row per SkillX enrolment with the learner's
  // verify / notify-CRO status. `summary` (from meta) counts each status for
  // the current filters, for the chips above the grid.
  admissionVerification: {
    data: [],
    meta: {},
    summary: { total: 0, pending: 0, verified: 0, croNotified: 0 },
    loading: false,
    marking: false,
  },
};

export const getSkillxDashboard = createAsyncThunk(
  'skillxSlice/getSkillxDashboard',
  async ({ fromDate, toDate,}) => {
    const res = await apiRequest({
      method: 'get',
      url: `analytics/assessment-counts?fromDate=${fromDate}&toDate=${toDate}&limit=no`,
    });
    return res;
  }
);

/* ------------------------------------------------------------------ */
/* IntervalSkillX — assignment authoring CRUD                          */
/* Hits the node-portal studentPortal controller (student-portal/...). */
/* ------------------------------------------------------------------ */

export const getSkillxAssignments = createAsyncThunk(
  'skillxSlice/getSkillxAssignments',
  async (params = {}) => {
    const { page = 1, limit = 10, search = '' } = params;
    const res = await apiRequest({
      method: 'get',
      url: `student-portal/skillx/get-assignments?page=${page}&limit=${limit}&search=${encodeURIComponent(
        search
      )}`,
    });
    return res;
  }
);

export const createSkillxAssignment = createAsyncThunk(
  'skillxSlice/createSkillxAssignment',
  async (data) => {
    const res = await apiRequest({
      method: 'post',
      url: `student-portal/skillx/create-assignment`,
      data,
    });
    return res;
  }
);

export const updateSkillxAssignment = createAsyncThunk(
  'skillxSlice/updateSkillxAssignment',
  async ({ id, ...data }) => {
    const res = await apiRequest({
      method: 'patch',
      url: `student-portal/skillx/update-assignment/${id}`,
      data,
    });
    return res;
  }
);

export const deleteSkillxAssignment = createAsyncThunk(
  'skillxSlice/deleteSkillxAssignment',
  async (id) => {
    const res = await apiRequest({
      method: 'delete',
      url: `student-portal/skillx/delete-assignment/${id}`,
    });
    return res;
  }
);

/* ------------------------------------------------------------------ */
/* IntervalSkillX — batch leaderboard (read-only over the XP ledger)   */
/* ------------------------------------------------------------------ */

// #1 of every batch that has XP → the "Top Performers by Batch" cards.
export const getSkillxBatchToppers = createAsyncThunk(
  'skillxSlice/getSkillxBatchToppers',
  async () => {
    const res = await apiRequest({
      method: 'get',
      url: `student-portal/skillx/leaderboard/batch-toppers`,
    });
    return res;
  }
);

// Full standings for one batch → the filtered standings table.
export const getSkillxBatchLeaderboard = createAsyncThunk(
  'skillxSlice/getSkillxBatchLeaderboard',
  async (batchId) => {
    const res = await apiRequest({
      method: 'get',
      url: `student-portal/skillx/leaderboard/batch/${batchId}`,
    });
    return res;
  }
);

/* ------------------------------------------------------------------ */
/* IntervalSkillX — XP configuration (admin) + manual awards (mentor)  */
/*                                                                     */
/* Rules are the point table an admin retunes; manual awards are XP a  */
/* mentor gives by hand, each with remarks, each revocable, all of it  */
/* audited. See public/docs/SKILLX_XP_PRODUCERS.md.                    */
/* ------------------------------------------------------------------ */

// The point table. `includeInactive` for the admin grid (retired rules still
// need to be visible); `manualOnly` for the award dialog's reason picker.
export const getSkillxXpRules = createAsyncThunk(
  'skillxSlice/getSkillxXpRules',
  async (params = {}) => {
    const { includeInactive = false, manualOnly = false } = params;
    const res = await apiRequest({
      method: 'get',
      url: `student-portal/skillx/xp/rules?includeInactive=${includeInactive}&manualOnly=${manualOnly}`,
    });
    return res;
  }
);

export const createSkillxXpRule = createAsyncThunk(
  'skillxSlice/createSkillxXpRule',
  async (data) => {
    const res = await apiRequest({
      method: 'post',
      url: `student-portal/skillx/xp/rules`,
      data,
    });
    return res;
  }
);

export const updateSkillxXpRule = createAsyncThunk(
  'skillxSlice/updateSkillxXpRule',
  async ({ id, ...data }) => {
    const res = await apiRequest({
      method: 'patch',
      url: `student-portal/skillx/xp/rules/${id}`,
      data,
    });
    return res;
  }
);

// Deactivates rather than deletes once the rule has awarded anything — the
// backend decides which, and says so in the response message.
export const deleteSkillxXpRule = createAsyncThunk(
  'skillxSlice/deleteSkillxXpRule',
  async (id) => {
    const res = await apiRequest({
      method: 'delete',
      url: `student-portal/skillx/xp/rules/${id}`,
    });
    return res;
  }
);

// Award XP by hand. Body: { admnNo, batchId, ruleKey, points?, remarks }.
export const awardSkillxManualXp = createAsyncThunk(
  'skillxSlice/awardSkillxManualXp',
  async (data) => {
    const res = await apiRequest({
      method: 'post',
      url: `student-portal/skillx/xp/manual-award`,
      data,
    });
    return res;
  }
);

// The audit history, server-paginated. Every filter is optional.
export const getSkillxManualXpAwards = createAsyncThunk(
  'skillxSlice/getSkillxManualXpAwards',
  async (params = {}) => {
    const {
      page = 1,
      limit = 10,
      search = '',
      batchId = '',
      admnNo = '',
      awardedBy = '',
      ruleKey = '',
      status = 'ALL',
      fromDate = '',
      toDate = '',
    } = params;

    const query = new URLSearchParams({
      page,
      limit,
      search,
      status,
      ...(batchId ? { batchId } : {}),
      ...(admnNo ? { admnNo } : {}),
      ...(awardedBy ? { awardedBy } : {}),
      ...(ruleKey ? { ruleKey } : {}),
      ...(fromDate ? { fromDate } : {}),
      ...(toDate ? { toDate } : {}),
    });

    const res = await apiRequest({
      method: 'get',
      url: `student-portal/skillx/xp/manual-awards?${query.toString()}`,
    });
    return res;
  }
);

// Take an award back: deletes its ledger row, subtracts the points, and keeps
// the audit row marked REVOKED with the reason.
export const revokeSkillxManualXpAward = createAsyncThunk(
  'skillxSlice/revokeSkillxManualXpAward',
  async ({ id, reason }) => {
    const res = await apiRequest({
      method: 'patch',
      url: `student-portal/skillx/xp/manual-awards/${id}/revoke`,
      data: { reason },
    });
    return res;
  }
);

// One learner's XP total, breakdown and recent hand-awards — the "before you
// award" panel, so a mentor can see what they already have.
export const getSkillxLearnerXp = createAsyncThunk(
  'skillxSlice/getSkillxLearnerXp',
  async ({ admnNo, batchId }) => {
    const res = await apiRequest({
      method: 'get',
      url: `student-portal/skillx/xp/learner/${admnNo}?batchId=${batchId || ''}`,
    });
    return res;
  }
);

/* ---------------------------- common badges ------------------------------ *
 *
 * Tier 2 of the SkillX badge model. Module badges (tier 1) are NOT here and
 * have no endpoint: they are earned automatically when a mentor approves the
 * project's submission in the assignments area, and the student portal derives
 * them from that approval rather than storing an award. Only the cross-cutting
 * badges — Perfect Attendance, Community Helper, Presentation Star — are given
 * by hand, and only those are reachable below.
 *
 * A learner may hold the same common badge more than once, so awarding one they
 * already have is a valid action rather than a duplicate to block.
 */

// What can be awarded: achievement_badges rows with assignment_id NULL.
export const getSkillxCommonBadges = createAsyncThunk(
  'skillxSlice/getSkillxCommonBadges',
  async () => {
    const res = await apiRequest({
      method: 'get',
      url: `student-portal/skillx/common-badges`,
    });
    return res;
  }
);

// One batch's learners, each with the common badges they currently hold.
export const getSkillxBatchBadgeAwards = createAsyncThunk(
  'skillxSlice/getSkillxBatchBadgeAwards',
  async (batchId) => {
    const res = await apiRequest({
      method: 'get',
      url: `student-portal/skillx/common-badges/batch/${batchId}`,
    });
    return res;
  }
);

// `note` is required by the server — the learner is shown it next to the badge.
export const awardSkillxCommonBadge = createAsyncThunk(
  'skillxSlice/awardSkillxCommonBadge',
  async (data) => {
    const res = await apiRequest({
      method: 'post',
      url: `student-portal/skillx/common-badges/award`,
      data,
    });
    return res;
  }
);

// Keeps the row and flips its status, same contract as the manual XP revoke.
export const revokeSkillxCommonBadge = createAsyncThunk(
  'skillxSlice/revokeSkillxCommonBadge',
  async ({ awardId, reason }) => {
    const res = await apiRequest({
      method: 'patch',
      url: `student-portal/skillx/common-badges/${awardId}/revoke`,
      data: { reason },
    });
    return res;
  }
);

/* ------------------------------------------------------------------ */
/* IntervalSkillX — referral leads (sales view)                        */
/*                                                                     */
/* The sales-side half of the Student Portal's "Refer & Earn". Rows are */
/* created there and worked through the ERP's existing lead screens, so */
/* these are reads only — there is no update thunk to add.             */
/* ------------------------------------------------------------------ */

export const getSkillxReferralLeads = createAsyncThunk(
  'skillxSlice/getSkillxReferralLeads',
  async (dispatchParams) => {
    const { paginationData, fromDate, toDate, status = '' } = dispatchParams;
    const paginationUrl = getPaginationUrl(paginationData);
    const res = await apiRequest({
      method: 'get',
      url: `student-portal/skillx/referral-leads${paginationUrl}&fromDate=${fromDate}&toDate=${toDate}&status=${status}`,
    });
    return res;
  }
);

// The four figures above the grid. A separate read because they count every
// referral in scope, not the ten rows on the current page.
export const getSkillxReferralLeadsSummary = createAsyncThunk(
  'skillxSlice/getSkillxReferralLeadsSummary',
  async ({ fromDate, toDate }) => {
    const res = await apiRequest({
      method: 'get',
      url: `student-portal/skillx/referral-leads/summary?fromDate=${fromDate}&toDate=${toDate}`,
    });
    return res;
  }
);

/* ------------------------------------------------------------------ */
/* IntervalSkillX — students roster + SkillX id                        */
/*                                                                     */
/* A SkillX student is one with an enrolment on a course belonging to  */
/* the SkillX project — there is no flag to read, so the server does   */
/* the join. The only thing this screen writes is student.skillx_id.   */
/* ------------------------------------------------------------------ */

/** Was this read the export's unpaged one, rather than the grid's page? */
const isUnpagedRead = (action) =>
  action?.meta?.arg?.paginationData?.gridSize === 'no';

export const getSkillxStudents = createAsyncThunk(
  'skillxSlice/getSkillxStudents',
  async (dispatchParams) => {
    const { paginationData, fromDate = '', toDate = '', linked = '' } = dispatchParams;
    const paginationUrl = getPaginationUrl(paginationData);
    const res = await apiRequest({
      method: 'get',
      url: `skillx-students${paginationUrl}&fromDate=${fromDate}&toDate=${toDate}&linked=${linked}`,
    });
    return res;
  }
);

// The three figures above the grid. Separate from the page because they count
// every SkillX learner in the date range, not the ten rows on screen.
export const getSkillxStudentsSummary = createAsyncThunk(
  'skillxSlice/getSkillxStudentsSummary',
  async ({ fromDate = '', toDate = '' }) => {
    const res = await apiRequest({
      method: 'get',
      url: `skillx-students/summary?fromDate=${fromDate}&toDate=${toDate}`,
    });
    return res;
  }
);

// Add, change or clear one learner's SkillX id. An empty string clears it —
// the only way back from a typo that has already been saved.
export const updateSkillxId = createAsyncThunk(
  'skillxSlice/updateSkillxId',
  async ({ admn_no: admnNo, skillx_id: skillxId }) => {
    const res = await apiRequest({
      method: 'patch',
      url: `skillx-students/${admnNo}/skillx-id`,
      data: { skillx_id: skillxId },
    });
    return res;
  }
);

// Upload a sheet of { row, admn_no, skillx_id }. With dryRun the server runs
// every check and writes nothing; without it, it writes all rows or none.
export const bulkUpdateSkillxIds = createAsyncThunk(
  'skillxSlice/bulkUpdateSkillxIds',
  async ({ rows, dryRun = false }) => {
    const res = await apiRequest({
      method: 'post',
      url: `skillx-students/skillx-id/bulk`,
      data: { rows, dry_run: dryRun },
    });
    return res;
  }
);

/* ------------------------------------------------------------------ */
/* IntervalSkillX — CRO attendance register                            */
/*                                                                     */
/* Read only. One batch, one month, a column per day. The statuses are */
/* resolved on the server so the CRO and the learner cannot disagree.  */
/* ------------------------------------------------------------------ */

export const getSkillxAttendanceBatches = createAsyncThunk(
  'skillxSlice/getSkillxAttendanceBatches',
  async () => {
    const res = await apiRequest({ method: 'get', url: `skillx-attendance/batches` });
    return res;
  }
);

export const getSkillxAttendanceRegister = createAsyncThunk(
  'skillxSlice/getSkillxAttendanceRegister',
  async ({ batchId, year, month }) => {
    const res = await apiRequest({
      method: 'get',
      url: `skillx-attendance/register?batch_id=${batchId}&year=${year}&month=${month}`,
    });
    return res;
  }
);

/* ------------------------------------------------------------------ */
/* IntervalSkillX — admission verification register                    */
/*                                                                     */
/* Learners verify their admission details in the Student Portal or    */
/* report a problem to their batch CRO. The CRO marks a reported row   */
/* verified here once it is fixed.                                     */
/* ------------------------------------------------------------------ */

export const getSkillxAdmissionVerifications = createAsyncThunk(
  'skillxSlice/getSkillxAdmissionVerifications',
  async (dispatchParams) => {
    const { paginationData, status = 'all', batchId = '', fromDate = '', toDate = '' } = dispatchParams;
    const paginationUrl = getPaginationUrl(paginationData);
    const res = await apiRequest({
      method: 'get',
      url: `skillx-admission-verification${paginationUrl}&status=${status}&batchId=${batchId}&fromDate=${fromDate}&toDate=${toDate}`,
    });
    return res;
  }
);

export const markSkillxAdmissionVerified = createAsyncThunk(
  'skillxSlice/markSkillxAdmissionVerified',
  async ({ courseEnrollmentId }) => {
    const res = await apiRequest({
      method: 'patch',
      url: `skillx-admission-verification/${courseEnrollmentId}/mark-verified`,
    });
    return res;
  }
);

const skillxSlice = createSlice({
  name: 'skillxSlice',
  initialState,
  reducers: {
    clearSkillxDashboard: (state) => {
      state.resData = {
        data: [],
        meta: {},
      };
    },
  },
  extraReducers: (builder) => {
    //..................GETING DASHBOARD FOR SKILLX................
    builder.addCase(getSkillxDashboard.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });

    builder.addCase(getSkillxDashboard.fulfilled, (state, action) => {
      state.loading = false;
      state.resData = {
        data: action?.payload?.data || [],
        meta: action?.payload?.meta || {},
      };
    });

    //..................GET ALL SKILLX ASSIGNMENTS................
    builder.addCase(getSkillxAssignments.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSkillxAssignments.fulfilled, (state, action) => {
      state.loading = false;
      state.assignments = {
        data: action?.payload?.data || [],
        meta: action?.payload?.meta || {},
      };
    });
    builder.addCase(getSkillxAssignments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    //..................CREATE / UPDATE / DELETE ASSIGNMENT................
    // The list is refreshed by the view after each mutation, so these only
    // drive the loading flag the modals read for their button state.
    builder.addCase(createSkillxAssignment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createSkillxAssignment.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createSkillxAssignment.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateSkillxAssignment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateSkillxAssignment.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateSkillxAssignment.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(deleteSkillxAssignment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteSkillxAssignment.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteSkillxAssignment.rejected, (state) => {
      state.loading = false;
    });

    //..................XP RULES (admin configuration)................
    builder.addCase(getSkillxXpRules.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSkillxXpRules.fulfilled, (state, action) => {
      state.loading = false;
      state.xpRules = {
        data: action?.payload?.data || [],
        meta: action?.payload?.meta || {},
      };
    });
    builder.addCase(getSkillxXpRules.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // ..................COMMON BADGES................
    builder.addCase(getSkillxCommonBadges.fulfilled, (state, action) => {
      state.commonBadges.catalogue = action?.payload?.data || [];
    });

    builder.addCase(getSkillxBatchBadgeAwards.pending, (state) => {
      state.commonBadges.loading = true;
      // Dropped rather than left in place: the roster belongs to one batch, and
      // the page counts badges off it in the header. Keeping the old batch's
      // learners through the load would attribute their awards to the new one.
      state.commonBadges.roster = [];
    });
    builder.addCase(getSkillxBatchBadgeAwards.fulfilled, (state, action) => {
      state.commonBadges.loading = false;
      state.commonBadges.roster = action?.payload?.data?.learners || [];
    });
    builder.addCase(getSkillxBatchBadgeAwards.rejected, (state, action) => {
      state.commonBadges.loading = false;
      state.commonBadges.roster = [];
      state.error = action.error.message;
    });

    //..................XP MANUAL AWARDS (mentor audit history)................
    builder.addCase(getSkillxManualXpAwards.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSkillxManualXpAwards.fulfilled, (state, action) => {
      state.loading = false;
      state.xpAwards = {
        data: action?.payload?.data || [],
        meta: action?.payload?.meta || {},
      };
    });
    builder.addCase(getSkillxManualXpAwards.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    //..................XP MUTATIONS................
    // Each view refetches after a mutation, so these only drive the loading
    //..................SKILLX STUDENTS (roster + SkillX id)................
    // The grid's Export button reuses this thunk with gridSize 'no' to pull
    // every filtered row at once. That response must NOT land in state: it
    // would replace the page on screen with the whole list and reset the pager
    // to one page, so exporting would silently change what the user is looking
    // at. The three cases below ignore an unpaged read for that reason.
    builder.addCase(getSkillxStudents.pending, (state, action) => {
      if (isUnpagedRead(action)) return;
      state.students.loading = true;
    });
    builder.addCase(getSkillxStudents.fulfilled, (state, action) => {
      if (isUnpagedRead(action)) return;
      state.students.loading = false;
      state.students.data = action?.payload?.data || [];
      state.students.meta = action?.payload?.meta || {};
    });
    builder.addCase(getSkillxStudents.rejected, (state, action) => {
      if (isUnpagedRead(action)) return;
      state.students.loading = false;
    });

    builder.addCase(getSkillxStudentsSummary.fulfilled, (state, action) => {
      state.students.summary = {
        ...state.students.summary,
        ...(action?.payload?.data || {}),
      };
    });

    // The dialog refetches the list itself, so this only drives its button.
    builder.addCase(updateSkillxId.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateSkillxId.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateSkillxId.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(bulkUpdateSkillxIds.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(bulkUpdateSkillxIds.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(bulkUpdateSkillxIds.rejected, (state) => {
      state.loading = false;
    });

    //..................ATTENDANCE REGISTER (CRO month grid)................
    builder.addCase(getSkillxAttendanceBatches.fulfilled, (state, action) => {
      state.attendance.batches = action?.payload?.data || [];
    });

    //..................SKILLX ADMISSION VERIFICATION................
    // Export reuses the list thunk with gridSize 'no'; that unpaged response
    // must not replace the page on screen (same rule as SkillX students).
    builder.addCase(getSkillxAdmissionVerifications.pending, (state, action) => {
      if (isUnpagedRead(action)) return;
      state.admissionVerification.loading = true;
    });
    builder.addCase(getSkillxAdmissionVerifications.fulfilled, (state, action) => {
      if (isUnpagedRead(action)) return;
      state.admissionVerification.loading = false;
      state.admissionVerification.data = action?.payload?.data || [];
      state.admissionVerification.meta = action?.payload?.meta || {};
      state.admissionVerification.summary = {
        ...state.admissionVerification.summary,
        ...(action?.payload?.meta?.summary || {}),
      };
    });
    builder.addCase(getSkillxAdmissionVerifications.rejected, (state, action) => {
      if (isUnpagedRead(action)) return;
      state.admissionVerification.loading = false;
    });

    builder.addCase(markSkillxAdmissionVerified.pending, (state) => {
      state.admissionVerification.marking = true;
    });
    builder.addCase(markSkillxAdmissionVerified.fulfilled, (state) => {
      state.admissionVerification.marking = false;
    });
    builder.addCase(markSkillxAdmissionVerified.rejected, (state) => {
      state.admissionVerification.marking = false;
    });

    builder.addCase(getSkillxAttendanceRegister.pending, (state) => {
      state.attendance.loading = true;
      // Dropped rather than left up: the rows belong to one batch and one
      // month, and stale ones would be read as this month's register.
      state.attendance.rows = [];
    });
    builder.addCase(getSkillxAttendanceRegister.fulfilled, (state, action) => {
      state.attendance.loading = false;
      state.attendance.rows = action?.payload?.data || [];
      state.attendance.meta = action?.payload?.meta || {};
    });
    builder.addCase(getSkillxAttendanceRegister.rejected, (state) => {
      state.attendance.loading = false;
      state.attendance.rows = [];
    });

    //..................REFERRAL LEADS (sales view)................
    builder.addCase(getSkillxReferralLeads.pending, (state) => {
      state.referralLeads.loading = true;
    });
    builder.addCase(getSkillxReferralLeads.fulfilled, (state, action) => {
      state.referralLeads.loading = false;
      state.referralLeads.data = action?.payload?.data || [];
      state.referralLeads.meta = action?.payload?.meta || {};
    });
    builder.addCase(getSkillxReferralLeads.rejected, (state) => {
      state.referralLeads.loading = false;
    });

    builder.addCase(getSkillxReferralLeadsSummary.fulfilled, (state, action) => {
      state.referralLeads.summary = {
        ...state.referralLeads.summary,
        ...(action?.payload?.data || {}),
      };
    });

    // flag the dialogs read for their button state.
    [
      createSkillxXpRule,
      updateSkillxXpRule,
      deleteSkillxXpRule,
      awardSkillxManualXp,
      revokeSkillxManualXpAward,
      awardSkillxCommonBadge,
      revokeSkillxCommonBadge,
    ].forEach((thunk) => {
      builder.addCase(thunk.pending, (state) => {
        state.loading = true;
      });
      builder.addCase(thunk.fulfilled, (state) => {
        state.loading = false;
      });
      builder.addCase(thunk.rejected, (state) => {
        state.loading = false;
      });
    });
  },
});

export const { clearSkillxDashboard } = skillxSlice.actions;
export default skillxSlice.reducer;
